import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2, Facebook, Linkedin } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { blogPosts } from '../data/blogData';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ReactMarkdown from 'react-markdown';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language, isRTL } = useLanguage();

  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#060B14]">
        <Navbar />
        <div className="pt-32 text-center">
          <h1 className="text-2xl text-white mb-4">Post not found</h1>
          <Link to="/blog" className="text-[#25D366]">Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-AE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category.en === post.category.en)
    .slice(0, 2);

  const shareUrl = window.location.href;
  const shareText = post.title[language];

  return (
    <div className="min-h-screen bg-[#060B14]" data-testid="blog-post-page">
      <Navbar />
      
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh] mt-20">
        <img 
          src={post.image} 
          alt={post.title[language]}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060B14] via-[#060B14]/50 to-transparent" />
      </div>

      {/* Content */}
      <article className="relative -mt-32 px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/blog')}
            className={`flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}
            data-testid="back-to-blog"
          >
            <ArrowLeft size={20} className={isRTL ? 'rotate-180' : ''} />
            {t('blog.backToBlog')}
          </button>

          {/* Category */}
          <span className="inline-block px-3 py-1 bg-[#25D366] text-white text-sm font-medium rounded-full mb-4">
            {post.category[language]}
          </span>

          {/* Title */}
          <h1 
            className={`text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 ${isRTL ? 'text-right' : ''}`}
            style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif" }}
          >
            {post.title[language]}
          </h1>

          {/* Meta */}
          <div className={`flex flex-wrap items-center gap-6 text-slate-400 mb-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Calendar size={18} />
              {formatDate(post.date)}
            </span>
            <span className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Clock size={18} />
              {post.readTime[language]}
            </span>
          </div>

          {/* Share Buttons */}
          <div className={`flex items-center gap-4 mb-12 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className="text-slate-400 text-sm">Share:</span>
            <a 
              href={`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-[#25D366] rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity"
              aria-label="Share on WhatsApp"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>
            <a 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-[#1877F2] rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity"
              aria-label="Share on Facebook"
            >
              <Facebook size={16} />
            </a>
            <a 
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-[#0A66C2] rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity"
              aria-label="Share on LinkedIn"
            >
              <Linkedin size={16} />
            </a>
          </div>

          {/* Article Content */}
          <div 
            className={`prose prose-invert prose-lg max-w-none ${isRTL ? 'text-right' : ''}`}
            style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Manrope', sans-serif" }}
          >
            <ReactMarkdown
              components={{
                h2: ({node, ...props}) => (
                  <h2 
                    className="text-2xl font-bold text-white mt-10 mb-4" 
                    style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif" }}
                    {...props} 
                  />
                ),
                h3: ({node, ...props}) => (
                  <h3 
                    className="text-xl font-semibold text-white mt-8 mb-3" 
                    style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif" }}
                    {...props} 
                  />
                ),
                p: ({node, ...props}) => (
                  <p className="text-slate-300 leading-relaxed mb-4" {...props} />
                ),
                ul: ({node, ...props}) => (
                  <ul className={`text-slate-300 space-y-2 mb-4 ${isRTL ? 'pr-4' : 'pl-4'}`} {...props} />
                ),
                ol: ({node, ...props}) => (
                  <ol className={`text-slate-300 space-y-2 mb-4 ${isRTL ? 'pr-4' : 'pl-4'}`} {...props} />
                ),
                li: ({node, ...props}) => (
                  <li className="text-slate-300" {...props} />
                ),
                strong: ({node, ...props}) => (
                  <strong className="text-white font-semibold" {...props} />
                ),
              }}
            >
              {post.content[language]}
            </ReactMarkdown>
          </div>

          {/* CTA */}
          <div className="mt-16 p-8 bg-gradient-to-r from-[#25D366]/20 to-[#0A0F1C] border border-[#25D366]/30 rounded-2xl text-center">
            <h3 
              className="text-2xl font-bold text-white mb-4"
              style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif" }}
            >
              {language === 'ar' ? 'هل لديك مشروع في ذهنك؟' : 'Have a Project in Mind?'}
            </h3>
            <p className="text-slate-400 mb-6">
              {language === 'ar' 
                ? 'تواصل معنا للحصول على استشارة مجانية وعرض سعر مخصص.'
                : 'Contact us for a free consultation and customized quote.'}
            </p>
            <a
              href="https://wa.me/971566832840?text=Hello%2C%20I%20read%20your%20blog%20and%20I%27m%20interested%20in%20your%20services"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white font-semibold rounded-full hover:bg-[#20BD5A] transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {language === 'ar' ? 'تحدث معنا' : 'Chat with Us'}
            </a>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-16">
              <h3 
                className={`text-2xl font-bold text-white mb-8 ${isRTL ? 'text-right' : ''}`}
                style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif" }}
              >
                {language === 'ar' ? 'مقالات ذات صلة' : 'Related Posts'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map((relPost) => (
                  <Link 
                    key={relPost.id}
                    to={`/blog/${relPost.slug}`}
                    className="bg-[#0A0F1C] border border-white/10 rounded-xl overflow-hidden hover:border-[#25D366]/50 transition-all group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={relPost.image} 
                        alt={relPost.title[language]}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className={`p-4 ${isRTL ? 'text-right' : ''}`}>
                      <h4 className="text-white font-semibold group-hover:text-[#25D366] transition-colors line-clamp-2">
                        {relPost.title[language]}
                      </h4>
                      <p className="text-slate-500 text-sm mt-2">{relPost.readTime[language]}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
